// Crews redesign — single source of truth for all crew copy and config.
// Index-card copy comes verbatim from the design handoff; subpage copy
// (missions, projects, steps, FAQs) follows the handoff's per-crew structure.

export interface CrewFeature {
  title: string;
  body: string;
}

export interface CrewProject {
  title: string;
  body: string;
}

export interface CrewStep {
  title: string;
  body: string;
}

export interface CrewFaq {
  q: string;
  a: string;
}

export interface CrewSpotlight {
  name: string;
  role: string;
  quote: string;
  photo?: string;
}

export interface Crew {
  slug: string;
  href: string;
  number: string;
  name: string;
  icon: string;
  tagline: string;
  body: string;
  director?: string;
  image: { src?: string; alt: string; caption: string };
  learnMore: CrewFeature[];
  mission: { paragraphs: string[]; quote: string };
  projects: CrewProject[];
  spotlight?: CrewSpotlight;
  joinSteps: CrewStep[];
  faqs: CrewFaq[];
}

// Brand accent used throughout via the --accent CSS custom property.
export const ACCENT = "#C77AD2";

// Toggles the "Director" chip on index cards (subpage spotlight always shows).
export const SHOW_DIRECTORS = true;

export const DISCORD_URL = "https://discord.gg/fPyTCBEP8P";

export const CREWS: Crew[] = [
  {
    slug: "apps",
    href: "/apps-crew",
    number: "01",
    name: "Apps Crew",
    icon: "smartphone",
    tagline: "Code that remembers, for minds that forget.",
    body: "We put brain health in everyone's pocket. From cognition tests to caregiver companions and family-facing resources, this hand-picked team, mentored by esteemed tech advisors, ships technology that empowers and connects.",
    director: "Bao",
    image: {
      src: "/assets/appscrewpic.webp",
      alt: "Cognition app UI",
      caption: "product shot: cognition app UI",
    },
    learnMore: [
      {
        title: "What you'll build",
        body: "Cognition tests, caregiver companions, and family-facing tools: real products with real users, shipped to the people who need them most.",
      },
      {
        title: "Who thrives here",
        body: "Developers, designers, and UX thinkers who want their code to matter. You don't need to be an expert. You need to care about the craft.",
      },
      {
        title: "The edge",
        body: "Mentorship from esteemed tech advisors, a hand-picked team, and a portfolio of work that genuinely helps people.",
      },
    ],
    mission: {
      paragraphs: [
        "Memory loss can make the world feel smaller. The Apps Crew exists to push back by building technology that keeps people connected to their own minds and to the people who love them. We design and ship tools for cognition testing, caregiver support, and family connection, treating every screen as a chance to give someone a little more agency.",
        "This is a hand-picked team mentored by esteemed tech advisors. Members work in small squads across design, engineering, and research, following a real product cycle from idea to release. The work is careful because the stakes are real: our users include people navigating memory loss right now.",
      ],
      quote: "We measure success in moments remembered, not downloads.",
    },
    projects: [
      {
        title: "Cognition Tools",
        body: "Accessible, research-informed tests that help users check in on their memory and cognition from any device.",
      },
      {
        title: "Caregiver Companion",
        body: "A support app for the people doing the caring: reminders, resources, and a calmer way to manage the day.",
      },
      {
        title: "Family Hub",
        body: "Family-facing resources that keep loved ones informed, connected, and involved in memory care.",
      },
    ],
    spotlight: {
      name: "Bao",
      role: "Apps Crew Director",
      quote:
        "The best thing we ship isn't an app. It's the feeling that someone out there built this for you.",
    },
    joinSteps: [
      {
        title: "Apply",
        body: "Tell us what you build and why memory matters to you. Applications are reviewed on a rolling basis.",
      },
      {
        title: "Pair up",
        body: "Get matched with a squad and a mentor, and pick up your first scoped issue.",
      },
      {
        title: "Ship",
        body: "Take a feature from design to release. Your code, in users' hands.",
      },
    ],
    faqs: [
      {
        q: "Do I need to be an experienced developer?",
        a: "No. We look for care and curiosity over credentials. Designers, researchers, and beginner coders all contribute. The team is selective, but selection is about commitment, not years of experience.",
      },
      {
        q: "What's the time commitment?",
        a: "Most members spend 3–5 hours a week, with a weekly squad sync. Weeks around a release can run a little heavier, and we plan those together.",
      },
      {
        q: "What tech stack does the crew use?",
        a: "It varies by project, but expect modern web and mobile tools. Mentors help you pick up whatever the project needs. Learning on the job is the point.",
      },
    ],
  },
  {
    slug: "ebooks",
    href: "/ebooks-crew",
    number: "02",
    name: "eBooks Crew",
    icon: "menu_book",
    tagline: "Turning neuroscience into pages the world can hold.",
    body: "A creative studio that curates, designs, and publishes digital books on memory and the brain, built to go global. Members shape layouts, develop themes, organize content, and host competitions that bring story and science to life.",
    // Director intentionally omitted — eBooks director unconfirmed.
    image: {
      src: "/assets/ebookscrewpic.webp",
      alt: "Memory Matters eBook cover spread",
      caption: "cover spread: Memory Matters eBook",
    },
    learnMore: [
      {
        title: "What you'll make",
        body: "Digital books on memory and the brain: layouts, themes, cover art, and the competitions that rally new writers around them.",
      },
      {
        title: "Where you'll shine",
        body: "Writers, editors, illustrators, and designers who love turning dense science into pages people actually want to read.",
      },
      {
        title: "The reach",
        body: "Our titles are digital-first and built to go global, so your spreads travel to readers far beyond your own city.",
      },
    ],
    mission: {
      paragraphs: [
        "Science that stays locked in journals can't help the families who need it. The eBooks Crew turns research on memory and the brain into digital books anyone can open, understand, and share: story and science, bound together.",
        "Members shape every part of a title: layouts, themes, structure, and the writing competitions that bring new voices in. Each book is designed to travel, to be shared, translated, and read far beyond where it was made.",
      ],
      quote: "A well-made page can carry science further than any lecture.",
    },
    projects: [
      {
        title: "Memory & the Brain Series",
        body: "Flagship digital titles that translate neuroscience into clear, beautifully designed chapters.",
      },
      {
        title: "Writing Competitions",
        body: "Themed contests that invite young writers worldwide to tell stories about memory. The best entries get published.",
      },
      {
        title: "Global Editions",
        body: "Translations and localized releases that carry our titles to readers around the world.",
      },
    ],
    // Spotlight omitted until the eBooks director is confirmed.
    joinSteps: [
      {
        title: "Apply",
        body: "Share a writing or design sample and the ideas you want to bring to the shelf.",
      },
      {
        title: "Join a title team",
        body: "Plug into a book in progress: layout, editing, art, or structure.",
      },
      {
        title: "Publish",
        body: "See your name in a finished title, released to readers worldwide.",
      },
    ],
    faqs: [
      {
        q: "Do I have to be a writer?",
        a: "Not at all. Books need editors, illustrators, layout designers, and organizers as much as writers. If you love making pages people want to read, there's a seat for you.",
      },
      {
        q: "How long does a book take?",
        a: "A typical title runs one to three months from outline to release, with clear milestones so you always know what's next.",
      },
      {
        q: "Can my work really go global?",
        a: "Yes. Our titles are digital-first and built to travel, and competitions draw entries from around the world.",
      },
    ],
  },
  {
    slug: "marketing",
    href: "/marketing-crew",
    number: "03",
    name: "Marketing Crew",
    icon: "campaign",
    tagline: "Awareness, amplified.",
    body: "We carry Memory Matters far and wide. The crew crafts compelling campaigns, runs the social channels, and creates content that raises awareness, builds community, and drives the engagement that fuels our mission.",
    director: "Isla Hildebrand",
    image: {
      src: "/featured/mm-instagram.jpg",
      alt: "Memory Matters Instagram grid (@memory_matters2025)",
      caption: "social grid: @memory_matters2025",
    },
    learnMore: [
      {
        title: "What you'll craft",
        body: "Campaigns, posts, and content that carry Memory Matters far and wide.",
      },
      {
        title: "Your toolkit",
        body: "Copywriting, design, analytics, and the social channels where awareness actually spreads.",
      },
      {
        title: "The result",
        body: "A louder mission: more reach, more engagement, and more people who understand memory loss.",
      },
    ],
    mission: {
      paragraphs: [
        "Awareness is the first treatment anyone receives. The Marketing Crew carries Memory Matters far and wide, crafting campaigns, running the social channels, and creating content that turns scrolling into understanding.",
        "Members own real channels and real campaigns, learning copywriting, design, and analytics by shipping every week. The result is a community that keeps growing and a mission that keeps getting louder.",
      ],
      quote: "Every post is a chance to make someone's memory matter.",
    },
    projects: [
      {
        title: "Social Channels",
        body: "Daily content across Instagram and beyond (@memory_matters2025) that keeps the mission visible.",
      },
      {
        title: "Awareness Campaigns",
        body: "Coordinated pushes around key moments: awareness months, launches, and events.",
      },
      {
        title: "Creative Studio",
        body: "Graphics, reels, and copy: the creative assets that power everything the organization publishes.",
      },
    ],
    spotlight: {
      name: "Isla Hildebrand",
      role: "Marketing Crew Director",
      quote:
        "Awareness compounds. One post becomes one conversation, and one conversation changes how a family faces memory loss.",
      photo: "/isla.webp",
    },
    joinSteps: [
      {
        title: "Apply",
        body: "Show us a post, a caption, or a campaign idea you're proud of.",
      },
      {
        title: "Join the calendar",
        body: "Take a slot in the content calendar and start shipping weekly.",
      },
      {
        title: "Publish & grow",
        body: "Watch your work reach thousands and learn what makes it travel.",
      },
    ],
    faqs: [
      {
        q: "Which platforms will I work on?",
        a: "Instagram (@memory_matters2025) is home base, with campaigns extending across our other channels and formats.",
      },
      {
        q: "Do I need design skills?",
        a: "Helpful but not required. The crew spans copywriters, designers, and analysts. You'll grow whichever craft you bring.",
      },
      {
        q: "Will I get creative freedom?",
        a: "Yes. You'll pitch ideas into the weekly calendar, and once a concept is approved, it's yours to make.",
      },
    ],
  },
];
